import React, { useLayoutEffect, useRef } from "react";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  Globe,
  Lock,
  Mail,
  Phone,
  Shield,
  Sparkles,
  UserCheck,
} from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTheme } from "../contexts/ThemeContext";

gsap.registerPlugin(ScrollTrigger);

const MAROON = "#2B0000";
const MAROON_DARK = "#190000";
const GOLD = "#D6B33F";
const GOLD_LIGHT = "#E9CD7A";
const CREAM = "#FBF6EF";
const SOFT_TEXT = "#6F625E";

const privacySections = [
  {
    icon: FileText,
    title: "1. Information We Collect",
    content: `We may collect personal information from you when you visit our site, register on the site, place an order, subscribe to our newsletter, or interact with us in other ways.`,
    details: [
      "Name and contact information (email, phone number, address)",
      "Payment information (processed securely through our payment partners)",
      "Order history and preferences",
      "Device and browser information",
      "IP address and location data",
    ],
  },
  {
    icon: Shield,
    title: "2. How We Use Your Information",
    content: `We use the information we collect to provide, maintain, and improve our services, as well as to communicate with you.`,
    details: [
      "Process your transactions and orders",
      "Improve customer service and support",
      "Send periodic emails regarding your order or other products and services",
      "Personalize your shopping experience",
      "Prevent fraud and ensure security",
    ],
  },
  {
    icon: Lock,
    title: "3. Data Protection & Security",
    content: `We implement a variety of security measures to maintain the safety of your personal information. However, no method of transmission over the internet or method of electronic storage is 100% secure.`,
    details: [
      "SSL encryption for all data transmission",
      "Secure payment gateways",
      "Regular security audits and updates",
      "Limited access to personal information",
      "Data retention policies and procedures",
    ],
  },
  {
    icon: UserCheck,
    title: "4. Your Rights",
    content: `You have the right to request access to the personal information we hold about you, to request corrections, and to request deletion of your personal information.`,
    details: [
      "Right to access your personal data",
      "Right to correct inaccurate data",
      "Right to request deletion of your data",
      "Right to withdraw consent at any time",
      "Right to data portability",
    ],
  },
  {
    icon: Globe,
    title: "5. Third-Party Services",
    content: `We may share your information with trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you.`,
    details: [
      "Payment processors and gateways",
      "Shipping and delivery partners",
      "Analytics and marketing services",
      "Customer support tools",
    ],
  },
  {
    icon: Mail,
    title: "6. Changes to This Privacy Policy",
    content: `We may update this privacy policy from time to time. We will notify you about significant changes in the way we treat personal information by sending a notice to the primary email address specified in your account or by placing a prominent notice on our site.`,
    details: [
      "Review policy updates regularly",
      "Significant changes will be communicated via email",
      "Continued use constitutes acceptance of updates",
      "Last updated: July 2026",
    ],
  },
];

export default function PrivacyPolicy() {
  const { theme } = useTheme();

  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const headingRef = useRef(null);
  const sectionsRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (reduceMotion) {
        gsap.set(
          [
            heroRef.current,
            headingRef.current,
            sectionsRef.current,
          ],
          {
            opacity: 1,
            x: 0,
            y: 0,
          }
        );
        return;
      }

      // Hero animation
      gsap.fromTo(
        heroRef.current,
        {
          opacity: 0,
          y: 35,
          scale: 0.985,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 1.05,
          ease: "power3.out",
        }
      );

      // Heading animation
      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          x: -45,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.9,
          delay: 0.15,
          ease: "power3.out",
        }
      );

      // Sections animation
      const sectionElements = gsap.utils.toArray(".privacy-section");
      gsap.fromTo(
        sectionElements,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.12,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionsRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Detail items animation
      const detailItems = gsap.utils.toArray(".privacy-detail-item");
      gsap.fromTo(
        detailItems,
        {
          opacity: 0,
          x: 20,
        },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.06,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionsRef.current,
            start: "top 70%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Parallax effect for hero image
      gsap.to(".privacy-hero-image", {
        yPercent: 8,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main
      ref={pageRef}
      className="min-h-screen overflow-hidden"
      style={{
        backgroundColor: theme?.user_bg || CREAM,
        color: theme?.user_text || MAROON_DARK,
      }}
    >
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="
          relative min-h-[380px] overflow-hidden
          border-b border-[#D6B33F]/20
          sm:min-h-[400px]
          lg:min-h-[300px]
        "
      >
        <div className="absolute inset-0">
          <img
            className="
              privacy-hero-image h-full w-full scale-[1.08] object-cover
              object-[55%_center]
              lg:scale-105 lg:object-center
            "
            src="https://images.unsplash.com/photo-1506765515384-028b60a970df?q=90&w=1800&auto=format&fit=crop"
            alt="Privacy and security"
          />
          <div
            className="
              absolute inset-0
              bg-gradient-to-b
              from-[#FBF6EF]/95 via-[#FBF6EF]/65 to-[#FBF6EF]/95
              lg:bg-gradient-to-r
              lg:from-[#FBF6EF] lg:via-[#FBF6EF]/40 lg:to-transparent
            "
          />
          <div className="absolute inset-0 bg-[#2B0000]/10" />
        </div>

        <div
          className="
            relative z-10 mx-auto flex min-h-[380px] max-w-[1420px]
            items-center px-4 pb-8 pt-12
            sm:min-h-[400px] sm:px-6 sm:pt-14
            lg:min-h-[300px] lg:px-14 lg:py-14
            xl:px-20
          "
        >
          <div
            ref={headingRef}
            className="
              w-full max-w-[600px] rounded-[24px]
              border border-white/60 bg-[#FBF6EF]/80
              px-4 py-5 shadow-[0_24px_70px_rgba(43,0,0,0.08)]
              backdrop-blur-[5px]
              sm:px-6 sm:py-7
              lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none
              lg:backdrop-blur-none
            "
          >
            <div
              className="
                mb-3 inline-flex items-center gap-2 rounded-full
                border border-[#D6B33F]/35 bg-white/65
                px-2.5 py-1 text-[9px] font-semibold uppercase
                tracking-[0.28em] sm:text-[10px] sm:px-3 sm:py-1.5
              "
              style={{ color: MAROON }}
            >
              <Sparkles size={13} style={{ color: GOLD }} />
              Your Privacy Matters
            </div>

            <h1
              className="
                font-serif text-[32px] leading-[1.05]
                sm:text-[42px] lg:text-[62px]
              "
              style={{ color: MAROON_DARK }}
            >
              Privacy Policy
            </h1>

            <div className="mt-4 flex items-center gap-3">
              <span className="h-px w-12" style={{ backgroundColor: GOLD }} />
              <span
                className="
                  flex h-6 w-6 rotate-45 items-center justify-center
                  rounded-[6px] border
                  sm:h-7 sm:w-7
                "
                style={{
                  borderColor: `${GOLD}80`,
                  backgroundColor: "#FFFDF9",
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-[2px] sm:h-2 sm:w-2"
                  style={{ backgroundColor: GOLD }}
                />
              </span>
              <span className="h-px w-12" style={{ backgroundColor: GOLD }} />
            </div>

            <p
              className="
                mt-4 text-sm leading-6
                sm:text-[15px] sm:leading-7
                lg:max-w-[520px]
              "
              style={{ color: SOFT_TEXT }}
            >
              Your privacy is important to us. This privacy policy outlines the
              types of personal information we collect, how we use it, and your
              rights regarding that information.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="relative px-4 py-10 sm:px-6 lg:px-14 lg:py-16 xl:px-20">
        <div className="pointer-events-none absolute left-0 top-0 h-72 w-72 rounded-full bg-[#D6B33F]/[0.05] blur-3xl" />

        <div
          className="relative mx-auto max-w-[1420px]"
          ref={sectionsRef}
        >
          {/* Privacy Sections */}
          <div className="space-y-6 sm:space-y-8">
            {privacySections.map(({ icon: Icon, title, content, details }) => (
              <div
                key={title}
                id={title.toLowerCase().replace(/\s+/g, "-")}
                className="
                  privacy-section scroll-mt-24 rounded-[20px]
                  border bg-white/55 p-5 shadow-[0_12px_35px_rgba(43,0,0,0.04)]
                  backdrop-blur-sm transition-all duration-300
                  hover:shadow-[0_18px_45px_rgba(43,0,0,0.08)]
                  sm:p-6
                  lg:p-8
                "
                style={{
                  borderColor: theme?.user_border || "#E8D9D0",
                }}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="
                      flex h-10 w-10 shrink-0 items-center justify-center
                      rounded-full
                      sm:h-12 sm:w-12
                    "
                    style={{
                      backgroundColor: `${GOLD}15`,
                      color: MAROON,
                    }}
                  >
                    <Icon size={18} strokeWidth={1.8} className="sm:w-[22px] sm:h-[22px]" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h2
                      className="font-serif text-xl font-semibold sm:text-2xl lg:text-3xl"
                      style={{ color: MAROON_DARK }}
                    >
                      {title}
                    </h2>

                    <p
                      className="mt-2 text-sm leading-6 sm:mt-3 sm:text-[15px] sm:leading-7"
                      style={{ color: theme?.user_text_soft || SOFT_TEXT }}
                    >
                      {content}
                    </p>

                    <ul className="mt-3 space-y-2 sm:mt-4 sm:space-y-2.5">
                      {details.map((detail) => (
                        <li
                          key={detail}
                          className="privacy-detail-item flex items-start gap-2.5 text-sm sm:text-[15px]"
                          style={{ color: theme?.user_text || MAROON_DARK }}
                        >
                          <CheckCircle
                            size={16}
                            className="mt-0.5 shrink-0 sm:w-[18px] sm:h-[18px]"
                            style={{ color: GOLD }}
                          />
                          <span className="break-words">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}

            {/* Contact Info at Bottom */}
            <div
              className="
                mt-6 rounded-[20px] border
                bg-gradient-to-br from-[#2B0000] to-[#190000]
                p-5 text-white
                sm:p-6
                lg:p-8
              "
              style={{
                borderColor: `${GOLD}30`,
              }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <Shield size={24} className="sm:w-[28px] sm:h-[28px]" style={{ color: GOLD_LIGHT }} />
                  <div>
                    <p className="font-serif text-base font-semibold sm:text-lg">
                      Privacy Commitment
                    </p>
                    <p className="text-xs sm:text-sm text-white/70">
                      We're committed to protecting your data
                    </p>
                  </div>
                </div>
                <a
                  href="mailto:support@slgold.com"
                  className="
                    group flex items-center gap-2 rounded-full
                    px-4 py-2 text-sm font-medium transition-all
                    duration-300 hover:translate-x-1
                    sm:px-5 sm:py-2.5
                  "
                  style={{
                    backgroundColor: GOLD,
                    color: MAROON_DARK,
                  }}
                >
                  Contact Privacy Team
                  <ArrowRight size={15} className="sm:w-[16px] sm:h-[16px]" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}